package com.rajen.angrejidictionary.data.remote.dto

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class AdminLoginRequest(
    val username: String,
    val password: String
)

@Serializable
data class AdminLoginResponse(
    val token: String,
    val expiresIn: Long
)

@Serializable
data class AdminApiResponse<T>(
    val success: Boolean,
    val data: T? = null,
    val error: AdminErrorDto? = null
)

@Serializable
data class AdminErrorDto(
    val type: String,
    val message: String
)

@Serializable
data class AdminWordListResponse(
    val words: List<AdminWordSummary>,
    val total: Long,
    val page: Int,
    val pageSize: Int
)

@Serializable
data class AdminWordSummary(
    val id: String,
    @SerialName("english_word") val englishWord: String,
    @SerialName("part_of_speech") val partOfSpeech: String,
    @SerialName("meaning_gujarati") val meaningGujarati: List<String>,
    @SerialName("category") val category: List<String> = emptyList(),
    // Always present in GET /admin/words; the default only guards older backends.
    @SerialName("is_premium") val isPremium: Boolean = false
)

/**
 * Matches the exact Grok JSON output schema.
 * Reuses existing DTOs from WordResponse.kt
 *
 * isPremium MUST default to null, not false. The Json instances here do not set
 * encodeDefaults, so a `false` default would be dropped from the request body, and on
 * update a missing field means "keep current value" (turning premium off would silently
 * do nothing). With a null default, true/false are sent and null is omitted.
 * Do not enable encodeDefaults or explicitNulls.
 */
@Serializable
data class CreateWordRequest(
    val word: WordInfoDto,
    @SerialName("word_forms_inflections") val wordForms: List<WordFormDto> = emptyList(),
    @SerialName("similar_words") val similarWords: List<RelatedWordDto> = emptyList(),
    @SerialName("opposite_words") val oppositeWords: List<RelatedWordDto> = emptyList(),
    val usage: UsageDto? = null,
    @SerialName("phrasal_usage") val phrasalUsage: PhrasalUsageDto? = null,
    @SerialName("tense_explanation_gujarati") val tenseExplanation: TenseExplanationDto? = null,
    @SerialName("is_premium") val isPremium: Boolean? = null
)

/**
 * Body for PUT /api/v1/admin/word-of-day.
 * wordId = null (or the field omitted) clears the override and resumes
 * the default daily rotation.
 */
@Serializable
data class WordOfDayRequest(
    @SerialName("word_id") val wordId: String? = null
)